import { createRoot } from 'react-dom/client';
import { HashRouter, Route, Routes, useParams } from 'react-router';
import { Provider } from 'react-redux';
import { IntlProvider } from 'react-intl';
import '@rio-cloud/iframe-resizer/child';

import packageJson from '../../package.json';
import { store } from './setup/store';
import { UIKIT } from './UIKIT';
import { APP_LAYOUT_PREVIEW_PATH } from './sites/components/appLayout/appLayoutPreviewRoute';
import { normalizeHashRoute } from './utils/hashRouting';

const CDN_URL = 'https://uikit.developers.rio.cloud';
const UIKIT_DEMO_THEME_CSS_ATTRIBUTE = 'data-uikit-demo-theme-css';

const themeCssFiles: Record<string, string> = {
    default: 'uikit-theme-default.css',
    rio: 'uikit-theme-rio.css',
    wireframes: 'uikit-theme-wireframes.css',
    man: 'uikit-theme-man.css',
    scania: 'uikit-theme-scania.css',
    traton: 'uikit-theme-traton.css',
    international: 'uikit-theme-international.css',
    vw: 'uikit-theme-vw.css',
};

const AppLayoutPreviewRoute = () => {
    const { previewId = '' } = useParams();

    return <UIKIT appLayoutPreviewId={previewId} />;
};

const getInitialThemeCssFile = () => {
    const brand = document.documentElement.dataset.brand?.toLowerCase() || 'default';

    return themeCssFiles[brand] || themeCssFiles.default;
};

const loadInitialThemeCss = () => {
    const themeCssFile = getInitialThemeCssFile();
    const href = import.meta.env.DEV
        ? `/uikit-theme-css/${themeCssFile}`
        : `${CDN_URL}/${packageJson.version}/${themeCssFile}`;
    const link = document.createElement('link');

    link.rel = 'stylesheet';
    link.href = href;
    link.setAttribute(UIKIT_DEMO_THEME_CSS_ATTRIBUTE, document.documentElement.dataset.brand || 'default');
    document.head.appendChild(link);
};

const renderDemo = () => {
    const container = document.getElementById('root') as HTMLDivElement;
    const root = createRoot(container);
    const normalizedHash = normalizeHashRoute(window.location.hash);

    if (normalizedHash !== window.location.hash) {
        window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}${normalizedHash}`);
    }

    root.render(
        <Provider store={store}>
            <IntlProvider locale='en-GB'>
                <HashRouter>
                    <Routes>
                        <Route path={`${APP_LAYOUT_PREVIEW_PATH}/:previewId`} element={<AppLayoutPreviewRoute />} />
                        <Route path='/*' element={<UIKIT />} />
                    </Routes>
                </HashRouter>
            </IntlProvider>
        </Provider>
    );
};

loadInitialThemeCss();
renderDemo();
